import { IHttpClient, FetchHttpClient } from "./HttpClient";
import { JsonService } from "./JsonService";

/**
 * Information about the language from the server
 */
export interface ILanguageInfo {
  /**
   * The display name of the language
   */
  displayName: string;

  /**
   * The IETF language tag
   */
  ietfLanguageTag: string;

  /**
   * Whether the language is RTL
   */
  isRightToLeft: boolean;

  /**
   * The native name of the language
   */
  nativeName: string;
}

/**
 * The server contract for settings
 */
export interface ISettingsModel {
  /**
   * The language
   */
  language?: string;

  /**
   * Whether autofill is enabled on the form
   */
  autoFillEnabled?: boolean;

  /**
   * The user's theme preference ("classic", "light", "dark", or "system")
   */
  themeName?: string;

  /**
   * The timestamps of when the various banners were dismissed.
   */
  bannersDismissed: { [bannerId: string]: Date };
}

/**
 * The settings service interface
 */
export interface ISettingsService {
  /**
   * Gets the available languages from the server
   *
   * @param language - The current UI language
   * @returns the available languages
   */
  getLanguagesAsync(language: string): Promise<ILanguageInfo[]>;

  /**
   * Save the settings
   *
   * @param settings - The settings to save
   * @param antiForgeryToken - The anti-forgery token
   * @returns a promise
   */
  saveSettingsAsync(settings: ISettingsModel, antiForgeryToken: string): Promise<void>;
}

/**
 * The settings service implementation
 */
export class SettingsService extends JsonService implements ISettingsService {
  /**
   * Initializes a new instance of the SettingsService class.
   *
   * @param apiVersion - The API version
   * @param httpClient - The optional HTTP client
   */
  public constructor(apiVersion: string, httpClient: IHttpClient | null = null) {
    super(apiVersion, httpClient ?? new FetchHttpClient());
  }

  /**
   * Gets the available languages from the server
   *
   * @param language - The current UI language
   * @returns the available languages
   */
  public getLanguagesAsync(language: string): Promise<ILanguageInfo[]> {
    return this.getJsonAsync(`/api/${this.apiVersion}/settings/languages?lang=${language}`);
  }

  /**
   * Save the settings to the server
   *
   * @param settings - The settings to save
   * @param antiForgeryToken - The anti-forgery token
   * @returns a Promise
   */
  public saveSettingsAsync(settings: ISettingsModel, antiForgeryToken: string): Promise<void> {
    return this.postJsonAsync(`/api/${this.apiVersion}/settings/save`, antiForgeryToken, settings);
  }
}
